import axios from "@/utils/axios";
import { WX_AUTH_API_PATH } from "@/utils/wechat";

enum Api {
  getInfo = "/wework/login/getInfo",
  bind = "/wework/login/bind",
  roomList = "/wework/meeting/room/list",
  roomReservationList = "/wework/meeting/room/reservation/list",
  reservationAdd = "/wework/meeting/reservation/add",
  reservationDetail = "/wework/meeting/reservation",
  reservationCancel = "/wework/meeting/reservation/cancel",
  reservationList = "/wework/meeting/reservation/list",
  personReservationList = "/wework/meeting/person/reservation/list",
  personList = "/wework/bsdData/person/list",
  // 企业微信 JS-SDK 签名（自定义转发到会话等客户端接口鉴权所需）
  jsApiSignature = "/wework/login/jsapi/signature",
}

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace Login {
  export interface BindReqForm {
    jobNum: string;
    name: string;
  }

  export interface UserInfoRespForm {
    personId?: number;
    name?: string;
    jobNum?: string;
    deptId?: number;
    deptName?: string;
    photoPath?: string;
    /** 登录失效时的后端授权地址（backend 模式下随 userInfo 存入 localStorage） */
    unauthorizedUrl?: string;
  }

  export interface PersonListItem {
    id?: string | number;
    personId?: string | number;
    companyPersonId?: string | number | null;
    companyId?: string | number | null;
    modulePersonId?: string | number | null;
    companyName?: string | null;
    moduleCode?: string | null;
    deptId?: string | number | null;
    deptCode?: string | null;
    deptName?: string | null;
    name?: string | null;
    jobNum?: string | null;
    cardNum?: string | null;
    personType?: string | null;
    postId?: string | number | null;
    postName?: string | null;
    photoPath?: string | null;
  }

  export interface RoomItem {
    roomId: number;
    roomName?: string;
    location?: string;
    capacity?: number;
    meetingInterval?: number;
    facilities?: string;
    roomStatus?: string;
  }

  export interface RoomReservationItem {
    reservationId: number | string;
    roomId?: number;
    roomName?: string;
    title?: string;
    startTime?: string;
    endTime?: string;
    meetingInterval?: number;
  }

  export interface ReservationAddForm {
    roomId: number;
    roomName?: string;
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    checkinStartTime?: string;
    checkinEndTime?: string;
    checkinRequiredForUnlock?: string;
    checkinMethod?: string;
    moderatorPersonId?: number;
    moderatorName?: string;
    attendees?: string;
    attendeeIds?: number[];
    reservationStatus?: string;
    status?: string;
    remark?: string;
  }

  export interface ReservationItem extends ReservationAddForm {
    reservationId: number | string;
    reservationStatusText?: string;
    checkinMethodText?: string;
    checkinRequiredText?: string;
  }

  export interface MeetingItem {
    reservationId?: number | string;
    meetingId?: number | string;
    id?: number | string;
    title?: string;
    meetingTitle?: string;
    reservationTitle?: string;
    roomName?: string;
    startTime?: string;
    endTime?: string;
    moderatorName?: string;
    reservationStatus?: string | number;
    reservationStatusText?: string;
    postId?: number | string;
    postCode?: string;
    postName?: string;
    [key: string]: unknown;
  }

  // 微信授权 code 换 token 的入参/出参
  export interface WxAuthForm {
    code: string;
  }
  export interface WxAuthResData {
    access_token: string;
    expires_in?: number;
    /**
     * 打开环境标识（同 UserInfoRespForm.channel）：
     *  - wxwork：企业微信内打开
     *  - wechat：微信内打开（分享链接场景）
     * 授权换 token 时即可由后端判定，路由守卫据此在进首页前提前分流
     */
    channel?: string;
  }
}

type PagedListResp<T> = {
  rows?: T[];
  list?: T[];
  records?: T[];
  total?: number;
  data?: T[] | T;
};

export const getInfo = () => axios.get<Login.UserInfoRespForm>(Api.getInfo);
export const bindUserInfo = (data: Login.BindReqForm) =>
  axios.post(Api.bind, data);

// 微信授权 code 换取 token（frontend 模式，无需携带旧 token）
// 注意：GET /wework/login?code=xxx；路径直接使用 WX_AUTH_API_PATH，与 axios 401 拦截器排除逻辑保持一致；
// 该接口与 JS-SDK 签名走独立认证服务（VUE_APP_SERVER_API 前缀）
export const wxAuth = (params: Login.WxAuthForm) => {
  return axios.server<Login.WxAuthResData>({
    url: WX_AUTH_API_PATH,
    method: "get",
    params,
    headers: {
      isToken: false,
    },
  });
};
export const getRoomList = () => axios.get<Login.RoomItem[]>(Api.roomList);
export const getRoomReservationList = (params: {
  roomId: number;
  bookingDate: string;
}) =>
  axios.get<{
    roomId: number;
    bookingDate: string;
    reservations: Login.RoomReservationItem[];
  }>(Api.roomReservationList, params);
export const addReservation = (data: Login.ReservationAddForm) =>
  axios.post(Api.reservationAdd, data);
export const getReservationDetail = (reservationId: string | number) =>
  axios.get<Login.ReservationItem>(`${Api.reservationDetail}/${reservationId}`);
export const cancelReservation = (data: { reservationId: string | number }) =>
  axios.post(Api.reservationCancel, data);
export const getReservationList = (params: {
  pageNum: number;
  pageSize: number;
  postId: number | string;
}) =>
  axios.get<PagedListResp<Login.ReservationItem>>(Api.reservationList, params);
export const getMyReservationList = (params: {
  pageNum: number;
  pageSize: number;
}) =>
  axios.get<PagedListResp<Login.ReservationItem>>(Api.reservationList, params);
export const getMyMeetingList = (params: {
  pageNum: number;
  pageSize: number;
}) =>
  axios.get<PagedListResp<Login.MeetingItem>>(
    Api.personReservationList,
    params
  );
export const getPersonList = (params: {
  name?: string;
  pageNum: number;
  pageSize: number;
}) => axios.get<PagedListResp<Login.PersonListItem>>(Api.personList, params);

// ===== 企业微信 JS-SDK =====

/** JS-SDK 签名（后端用 jsapi_ticket 对 url 计算 sha1 得到） */
export interface JsApiSignature {
  /** 企业 ID（CorpID） */
  corpId?: string;
  /** 自建应用 AgentId */
  agentId?: string | number;
  timestamp?: string | number;
  nonceStr?: string;
  /** 企业身份签名：wx.config / getConfigSignature 使用 */
  signature?: string;
  /** 以下为应用身份签名字段：agentConfig / getAgentConfigSignature 使用；缺省时前端复用企业签名 */
  agentTimestamp?: string | number;
  agentNonceStr?: string;
  agentSignature?: string;
}

/**
 * 获取 JS-SDK 签名（与 wxAuth 同走认证服务，避免业务网关对登录态/签名校验不一致）
 * @param params.url 当前页面地址，不含 # 及其后内容（与客户端校验的地址保持一致）
 */
export const getJsApiSignature = (params: { url: string }) =>
  axios.server<JsApiSignature>({
    url: Api.jsApiSignature,
    method: "get",
    params,
  });
